import { Fragment, memo, useMemo } from 'react'
import type { GridApi, GridColumn } from './types'
import type { GridLeafColumn } from './useGridState'
import { middleBounds } from './useColumnWindow'
import GridCell from './GridCell'
import classes from './grid-theme.module.scss'

export interface GridRowProps<T> {
  row: T
  rowId: string
  rowIndex: number
  leafColumns: GridLeafColumn<T>[]
  /** Width of the virtualized-away columns before / after the rendered slice. */
  leadSpacer?: number
  trailSpacer?: number
  api: GridApi<T>
  isPinnedBottom?: boolean
  isLast?: boolean
  depth?: number
  editingField: string | null
  /**
   * The row's staged edit, straight off the pending-edits Map. Passing the
   * object rather than a derived Set keeps the prop identity stable across
   * scroll frames, so an untouched row never re-renders.
   */
  pendingEdit?: Partial<T> | null
  /** Field whose cell shows the fill handle, when it is in this row. */
  fillHandleField?: string | null
  /** Set on the cell this row contributes to an in-progress fill drag. */
  fillPreviewField?: string | null
  fillPreviewEdge?: 'single' | 'top' | 'bottom' | 'middle' | null
  /** Whether the editing cell in this row should open its list on mount. */
  editorOpenList?: boolean
  onFillHandlePointerDown?: (event: React.PointerEvent, field: string) => void
  onFillHandleDoubleClick?: (event: React.MouseEvent, field: string) => void
  /** The focused cell's field, when the focus ring is on this row. */
  focusedField?: string | null
  rowClassName?: string
  /**
   * Virtualized rows take their offset as a number instead of a style object:
   * a fresh object literal each frame would break memo for every visible row
   * and re-render the whole viewport on every scroll tick.
   */
  offsetY?: number
  minHeight?: number
  style?: React.CSSProperties
  virtual?: boolean
  onCellClick: (event: React.MouseEvent, rowId: string, field: string, editable: boolean) => void
  onCommit: (
    rowId: string,
    column: GridColumn<T>,
    row: T,
    value: unknown,
    baseline: unknown,
  ) => void
  onCancelEdit: () => void
  onKeyDown?: (event: React.KeyboardEvent, rowId: string, field: string, row: T) => void
  /** Set by the virtualizer for dynamic row measurement. */
  measureRef?: (el: HTMLElement | null) => void
}

const GridRowInner = <T,>({
  row,
  rowId,
  rowIndex,
  leafColumns,
  leadSpacer,
  trailSpacer,
  api,
  isPinnedBottom = false,
  isLast = false,
  depth = 0,
  editingField,
  pendingEdit,
  fillHandleField = null,
  fillPreviewField = null,
  fillPreviewEdge = null,
  editorOpenList = false,
  onFillHandlePointerDown,
  onFillHandleDoubleClick,
  focusedField = null,
  rowClassName,
  offsetY,
  minHeight,
  style,
  virtual = false,
  onCellClick,
  onCommit,
  onCancelEdit,
  onKeyDown,
  measureRef,
}: GridRowProps<T>) => {
  const bounds = useMemo(() => middleBounds(leafColumns), [leafColumns])

  // `minHeight` applies with or without an offset. An autoHeight grid has no
  // virtualizer and so no offset, but its rows still owe the caller the
  // rowHeight it asked for — without this they collapse to their text.
  const rowStyle = useMemo(() => {
    if (offsetY != null) {
      return { transform: `translateY(${offsetY}px)`, minHeight }
    }
    return minHeight == null ? style : { ...style, minHeight }
  }, [offsetY, minHeight, style])

  return (
  <div
    ref={measureRef}
    className={[
      classes.row,
      virtual ? classes.rowVirtual : '',
      isLast ? classes.rowLast : '',
      rowClassName ?? '',
    ]
      .filter(Boolean)
      .join(' ')}
    style={rowStyle}
    data-row-id={rowId}
    data-row-index={rowIndex}
    // The virtualizer's measureElement identifies rows by this attribute —
    // without it, dynamic row heights (wrapped/error cells) never reach the
    // offset math and taller rows overlap the ones below.
    data-index={rowIndex}
    role="row"
  >
    {leafColumns.map((leaf, index) => (
      <Fragment key={leaf.field}>
      {leadSpacer && index === bounds.first ? (
        <div style={{ flex: '0 0 auto', width: leadSpacer }} aria-hidden />
      ) : null}
      <GridCell
        column={leaf.column}
        row={row}
        rowId={rowId}
        rowIndex={rowIndex}
        width={leaf.width}
        api={api}
        isPinnedBottom={isPinnedBottom}
        isEditing={editingField === leaf.field}
        editorOpenList={editingField === leaf.field && editorOpenList}
        isChanged={pendingEdit != null && leaf.field in pendingEdit}
        isFocused={focusedField === leaf.field}
        showFillHandle={fillHandleField === leaf.field}
        inFillPreview={fillPreviewField === leaf.field}
        fillPreviewEdge={fillPreviewEdge}
        onFillHandlePointerDown={
          onFillHandlePointerDown
            ? (event) => onFillHandlePointerDown(event, leaf.field)
            : undefined
        }
        onFillHandleDoubleClick={
          onFillHandleDoubleClick
            ? (event) => onFillHandleDoubleClick(event, leaf.field)
            : undefined
        }
        depth={depth}
        pinned={leaf.pinned}
        offset={leaf.offset}
        isPinnedEdge={leaf.isPinnedEdge}
        onCellClick={onCellClick}
        onCommit={onCommit}
        onCancelEdit={onCancelEdit}
        onKeyDown={onKeyDown}
      />
      {trailSpacer && index === bounds.last ? (
        <div style={{ flex: '0 0 auto', width: trailSpacer }} aria-hidden />
      ) : null}
      </Fragment>
    ))}
  </div>
  )
}

const GridRow = memo(GridRowInner) as typeof GridRowInner

export default GridRow
